import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './InstructoresModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Instructores";
const endpoint = ENDPOINTS.instructor;
const endpointById = ENDPOINTS.instructorById;
const modelo = Models.instructor;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    numeroCertificacion: f.numeroCertificacion,
    descripcionCertificacion: f.descripcionCertificacion,
    institucionQueCertifica: f.institucionQueCertifica,
    avatarUrl: f.avatarUrl,
    bannerUrl: f.bannerUrl,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idInstructor: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    numeroCertificacion: f.numeroCertificacion,
    descripcionCertificacion: f.descripcionCertificacion,
    institucionQueCertifica: f.institucionQueCertifica,
    avatarUrl: f.avatarUrl,
    bannerUrl: f.bannerUrl,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resInst, resEmp, resPer] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.empleados),
        axios.get(ENDPOINTS.personas)
      ]);
      setRows((resInst.data || []).map((x) => modelo(x)));
      setEmpleados((resEmp.data || []).map(x => Models.empleado(x)));
      setPersonas((resPer.data || []).map(x => Models.personas(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getEmpleadoNombre = (idEmpleado) => {
    const emp = empleados.find(e => e.idEmpleado === idEmpleado);
    if (!emp) return `Empleado ${idEmpleado}`;
    const per = personas.find(p => p.idPersona === emp.idPersona);
    return per ? `${per.nombre} ${per.apellidoPaterno} ${per.apellidoMaterno || ''}`.trim() : `Empleado ${idEmpleado}`;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const empNombre = getEmpleadoNombre(r.idEmpleado).toLowerCase();
      const num = (r.numeroCertificacion || '').toLowerCase();
      const desc = (r.descripcionCertificacion || '').toLowerCase();
      const inst = (r.institucionQueCertifica || '').toLowerCase();
      const ava = (r.avatarUrl || '').toLowerCase();
      const ban = (r.bannerUrl || '').toLowerCase();
      const act = r.activo ? 'si' : 'no';
      return empNombre.includes(q) || num.includes(q) || desc.includes(q) || inst.includes(q) || ava.includes(q) || ban.includes(q) || act.includes(q);
    });
  }, [rows, query, empleados, personas]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idInstructor === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idInstructor ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por empleado, certificación, institución, avatar o banner…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>No. Certificación</th>
                  <th>Descripción Certificación</th>
                  <th>Institución</th>

                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idInstructor}>
                    <td>{getEmpleadoNombre(r.idEmpleado)}</td>
                    <td><strong>{normalizeValue(r.numeroCertificacion)}</strong></td>
                    <td>{normalizeValue(r.descripcionCertificacion)}</td>
                    <td>{normalizeValue(r.institucionQueCertifica)}</td>

                    <td style={{ textAlign: 'center' }}>
                      <button type="button" className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(r.idInstructor)} aria-label="Editar" title="Editar">
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" className="rk-iconbtn rk-iconbtn--del" onClick={() => { setEditingRowId(null); const name = r.numeroCertificacion || `Instructor ${r.idInstructor}`; askDelete(r.idInstructor, name); }} aria-label="Borrar" title="Borrar">
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (<tr><td colSpan={9}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              empleados={empleados}
              personas={personas}
              instructores={rows}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
